import {
  ArrowPathIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Alert,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  rem,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { RoleItem } from "../../models/role.model";
import { RoleDictionaryAdminService } from "../../services/admin";

export function OperationsRolesDictionary() {
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<RoleItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRoles, setTotalRoles] = useState(0);

  const roleDictionaryService = new RoleDictionaryAdminService();

  // Load data
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await roleDictionaryService.getRolesDictionary();
      setRoles(data);
      setFilteredRoles(data);
      setTotalRoles(data.length);
    } catch (err) {
      console.error("Error loading roles:", err);
      setError(err instanceof Error ? err.message : "Failed to load roles");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter roles based on search query
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredRoles(roles);
    } else {
      const filtered = roles.filter((role) =>
        role.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
      setFilteredRoles(filtered);
    }
  }, [debouncedSearchQuery, roles]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Dictionaries", href: "/operations/dictionaries" },
              { label: "Roles & Positions", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Roles & Positions Dictionary
          </Title>
          <Text c="dimmed" size="lg">
            View all roles and positions entered by users in the system
          </Text>
        </div>

        {/* Read-only notification */}
        <Alert
          icon={<InformationCircleIcon style={{ width: rem(20) }} />}
          title="Read-Only Dictionary"
          color="blue"
        >
          <Text size="sm">
            This is not a traditional dictionary but a collection of all roles
            and positions that users have entered in the system. It is designed
            to provide maximum flexibility in how roles can be named while still
            allowing searching and filtering.
          </Text>
          <Text size="sm" mt="sm">
            Since roles are created directly by users, this view is read-only.
            New roles will appear here automatically as they are added to user
            profiles.
          </Text>
        </Alert>

        {/* Search and Actions */}
        <Group justify="space-between" align="flex-end">
          <TextInput
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon style={{ width: rem(18) }} />}
            rightSection={
              searchQuery ? (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <XMarkIcon style={{ width: rem(16) }} />
                </Button>
              ) : null
            }
            style={{ flex: 1, maxWidth: 400 }}
          />
          <Button
            leftSection={<ArrowPathIcon style={{ width: rem(16) }} />}
            onClick={loadData}
            variant="default"
            loading={isLoading}
          >
            Refresh
          </Button>
        </Group>

        {/* Statistics */}
        <Group>
          <Badge size="lg" variant="outline">
            Total: {totalRoles}
          </Badge>
          {searchQuery && (
            <Badge size="lg" variant="outline" color="blue">
              Filtered: {filteredRoles.length}
            </Badge>
          )}
        </Group>

        {/* Error state */}
        {error && (
          <Alert color="red" title="Error loading roles">
            {error}
            <Button onClick={loadData} mt="md" variant="outline" size="sm">
              Try Again
            </Button>
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && (
          <Center py="xl">
            <Stack align="center">
              <Loader size="lg" />
              <Text>Loading roles...</Text>
            </Stack>
          </Center>
        )}

        {/* Roles table */}
        {!isLoading && !error && (
          <Paper withBorder>
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: "80px" }}>Row No</Table.Th>
                    <Table.Th>Role Name</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredRoles.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={2}>
                        <Center py="xl">
                          <Text c="dimmed">
                            {searchQuery
                              ? "No roles match your search"
                              : "No roles found in system"}
                          </Text>
                        </Center>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    filteredRoles.map((role) => (
                      <Table.Tr key={role.id}>
                        <Table.Td>{role.id}</Table.Td>
                        <Table.Td>
                          <Text fw={500}>{role.name}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}

        {/* Help Card */}
        <Paper withBorder p="lg">
          <Stack gap="md">
            <Title order={3} size="h4">
              About Roles & Positions
            </Title>
            <Text size="sm">
              Roles and positions are entered directly by users when creating
              and editing their profiles or work experience. This approach
              provides maximum flexibility while still allowing standardized
              reporting.
            </Text>
            <Text size="sm">
              This view shows all unique roles that have been entered in the
              system, which can help administrators understand the types of
              roles represented across the organization.
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
